import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  MapPin,
  Building2,
  Shield,
  Award,
  Target,
  Briefcase,
  Calendar,
  Globe,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const EuropeNigerianDiaspora: React.FC = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  // Update page title and meta description
  useEffect(() => {
    document.title = "Nigerian Diaspora in Europe | Smart & Transparent Investments";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Empower your financial future with investment options for Nigerians in Europe. Explore safe, transparent, and impactful opportunities to grow wealth and support Nigeria.");
    }
  }, []);

  const faqs = [
    {
      id: 1,
      question: "What types of projects are available for Nigerians in Europe?",
      answer: "Europe-based Nigerians can invest in Nigerian real estate, agriculture, energy, and technology startups through DiasporaWise."
    },
    {
      id: 2,
      question: "How can Nigerians in Europe manage their investments remotely?",
      answer: "Investors receive digital dashboards, regular reports, and project tracking features to manage investments from anywhere in Europe."
    },
    {
      id: 3,
      question: "Is there support for Nigerians investing from different European countries?",
      answer: "Yes, DiasporaWise works with Nigerian professionals across Europe including Germany, France, Italy, and the Netherlands."
    },
    {
      id: 4,
      question: "Are the returns from Nigeria-based investments taxable in Europe?",
      answer: "Tax laws vary by country; investors are advised to consult local tax experts. DiasporaWise ensures proper documentation for compliance."
    },
    {
      id: 5,
      question: "Why should Nigerians in Europe invest back home?",
      answer: "Investing supports Nigeria's growth while helping diaspora members build long-term wealth with profitable opportunities."
    }
  ];



  const countries = [
    { name: "Germany", investors: "35,000+", description: "Largest Nigerian community in Europe, strong in tech and engineering" },
    { name: "Netherlands", investors: "25,000+", description: "Growing tech and business hub for Nigerian professionals" },
    { name: "France", investors: "20,000+", description: "Strong presence in finance and international organizations" },
    { name: "Italy", investors: "15,000+", description: "Growing community in fashion, design, and manufacturing" },
    { name: "Belgium", investors: "8,000+", description: "EU capital with opportunities in international organizations" },
    { name: "Sweden", investors: "6,000+", description: "Tech and innovation hub attracting Nigerian talent" }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "EU Regulatory Compliance",
      description: "Expert guidance on EU regulations, GDPR compliance, and cross-border investment requirements."
    },
    {
      icon: Globe,
      title: "Multi-Currency Support",
      description: "Support for EUR, GBP, and other European currencies with competitive exchange rates."
    },
    {
      icon: Award,
      title: "Local Expertise",
      description: "Dedicated European team with knowledge of diverse EU regulatory landscapes and business practices."
    },
    {
      icon: Users,
      title: "Pan-European Network",
      description: "Connect with Nigerian professionals across multiple European countries for networking and collaboration."
    }
  ];

  const investmentTypes = [
    {
      title: "Renewable Energy",
      description: "Solar, wind, and hydroelectric projects with EU funding support",
      returns: "12-20%",
      icon: Building2
    },
    {
      title: "Technology",
      description: "Startups and tech companies with EU market access",
      returns: "20-35%",
      icon: TrendingUp
    },
    {
      title: "Real Estate",
      description: "Premium properties in Lagos, Abuja, and other major cities",
      returns: "15-25%",
      icon: Target
    },
    {
      title: "Agriculture",
      description: "Modern farming and agribusiness with EU export potential",
      returns: "10-18%",
      icon: Briefcase
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
              <MapPin className="w-4 h-4" />
              Europe
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Nigerian Diaspora in Europe
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Empower your financial future with investment options for Nigerians in Europe. Explore safe, transparent, and impactful opportunities to grow wealth and support Nigeria.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.diasporawise.com"
                className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 lg:hidden"
              >
                <Briefcase className="w-5 h-5" />
                Start Investing Today
              </a>
              <Link
                to="/contact"
                className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Contact EU Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* European Countries */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nigerian Communities Across Europe
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with Nigerian professionals in major European countries and access local networking opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {countries.map((country, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{country.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{country.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Investment Opportunities */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Investment Opportunities for European Investors
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access to carefully curated investment opportunities with EU-specific considerations and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentTypes.map((investment, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <investment.icon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{investment.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{investment.description}</p>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                    {investment.returns} Returns
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EU-Specific Benefits */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              EU-Specific Benefits & Support
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored services and support designed specifically for European-based Nigerian investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* European Events & Meetups */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Upcoming European Events
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our networking events and investment seminars across Europe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                date: "March 20, 2024",
                title: "Berlin Investment Summit",
                location: "Berlin, Germany",
                description: "Connect with fellow investors and learn about new opportunities"
              },
              {
                date: "March 27, 2024",
                title: "Amsterdam Tech Investment Meetup",
                location: "Amsterdam, Netherlands",
                description: "Focus on technology and innovation investment opportunities"
              },
              {
                date: "April 3, 2024",
                title: "Paris Real Estate Seminar",
                location: "Paris, France",
                description: "Learn about real estate investment strategies and market trends"
              }
            ].map((event, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <span className="text-purple-600 font-semibold">{event.date}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{event.location}</span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{event.description}</p>
                <button className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors duration-300">
                  Register Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Invest from Europe?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of successful Nigerian professionals across Europe who are building wealth through strategic investments in Nigeria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.diasporawise.com"
              className="bg-white text-purple-700 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 lg:hidden"
            >
              <Target className="w-5 h-5" />
              Get Started Now
            </a>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact EU Team
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about investing in Nigeria from Europe"
        faqs={faqs}
      />

      {/* Learn More About Nigerian Diaspora Investment in Europe Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="europe-diaspora-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Diaspora Investment in Europe' : 'Learn More About Nigerian Diaspora Investment in Europe'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="europe-diaspora-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Introduction: Nigerians Living in Europe and Why They Are Investing Back Home
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Europe is home to one of the world's largest Nigerian diaspora populations, living in communities across the UK, Germany, France, Italy and Spain as well as other EU countries. Nigerians in Europe have sent back remittances that paid for education, health care and household maintenance at home for decades.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Remittances are nice, but they normally do not result in long-term financial wealth or national development. That's what makes Nigerian diaspora investment in Europe increasingly significant. Beyond remittance transfer, Nigerians in diaspora can now invest in Nigeria from Europe using a seamless, structured and secure mechanism as provided for by Diasporawise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These financial fitness investments empower European Nigerians to protect their own future while contributing to Nigeria's development through the creation of jobs, fortifying industries and erecting infrastructure.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Understanding Nigerian Diaspora Investment from Europe</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diaspora investments in Nigeria are not remittances. Instead of addressing immediate needs, it is centered on building sustainable assets and economic impact. For Nigerian professionals in Europe, it will mean investing money into vetted projects that offer financial returns and social development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Thanks to channels such as Diasporawise, Nigerians in Europe can safely invest in the most promising sectors of Nigeria—real estate, agriculture, technology, renewable energy, SMEs and infrastructure. They take out the risk of informal channels by giving you formal, transparent and professionally managed options.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For the Nigerian diaspora in Europe, investment is not merely an economic calculation. It is also about keeping a connection to home, maintaining a cultural tie and leaving behind a secure legacy for the future generations.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Top Sectors for Nigerians in Europe to Invest In</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Real Estate Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real Estate is one of the secure investment opportunities for Nigerians in Europe. According to a report by the Affordable Housing Institute, Nigeria needs about 17 million dwelling units and such other commercial real estate spaces.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  European-based diaspora investors can explore: Residential estates and apartment complexes. Commercial offices and retail centers. Land acquisition and development projects.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real estate investment in Nigeria enables those living in Europe to increase their net worth through rental income and property appreciation.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Agriculture Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Despite severe under-investment over the years, agriculture continues to be a major contributor to Nigeria's economy and presents lucrative business prospects for diaspora investors. For Nigerian professionals in Europe, like Omolayo, investment in agriculture equals money and national development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Popular investments include: Crop production and large-scale farming. Food processing, packaging, and export. Agribusiness ventures with export potential.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through investing in agriculture, diaspora investors would be contributing to food security, job creation and rural development – and making money at the same time.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Technology & Startups</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's tech industry is the Silicon Valley of Africa, with a booming scene of fintech, health-tech and e-commerce startups. For diaspora investors in Europe, investing in technology is an opportunity to be part of the innovation and earn substantial financial rewards.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Opportunities for European Nigerians include: Backing early-stage startups. Funding scalable digital platforms. Supporting fintech and health-tech ventures.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Having the ability to access European markets and pool together professional skills, diaspora investors can play a role in facilitating the growth of Nigerian startups into international markets; creating profitable investments in Nigeria.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Renewable Energy Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's energy deficit is one of the fastest-growing opportunities to deploy renewable energy. That sector is a boon for the Nigerians in Europe who are able to make money while also making a positive, lasting social impact.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Renewable investment options include: Solar power projects. Wind energy solutions. Off-grid clean energy startups.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diaspora investors in Europe contribute to energy access, sustainability and economic resilience by investing in renewable energy development challenges in Nigeria.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">SMEs and Infrastructure</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  SMEs and infrastructure are Nigerian's economic backbone. For Nigerians in diaspora in Europe, participation in these sectors is a vote for entrepreneurship and nation-building.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Key opportunities include: SME financing and expansion. Transport and logistics. Communication and digital infrastructure.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With SME and infrastructure investment, Nigerians in Europe make job creation, industrial growth and sustained economic development happen.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of Diaspora Investment for European Nigerians</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  An investment in Nigeria from Europe has several advantages such as:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Wealth Creation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Go beyond one-off remittances and create assets.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safe and Transparent</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With a company such as Diasporawise, investments are regulated and trusted.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Build A Nation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Help develop Nigeria by funding businesses, jobs and infrastructure.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Culture Keeping</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Stay connected with Nigeria, while you live abroad.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Family Wealth</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Establish a legacy for children, grandchildren and great-grandchildren.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For many of the Nigerian professionals in Europe, investment from the diaspora is as much about maintaining a sense of patriotism and cultural connection as it is about ensuring financial security.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Start Investing in Nigeria from Europe</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through modern platforms like Diasporawise, the days of the process being difficult for Nigerians in Europe to invest back home is now history. It is an easy process, which is transparent and secure.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Steps for European diaspora investors:</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  1. Opt for a regulated platform such as Diasporawise, which caters exclusively to diaspora investors.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  2. Choose a sector you like the look of — real estate, agriculture, technology or renewable energy or SMEs and from socially good companies.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  3. Read more about the project and its associated risks and returns.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  4. Invest directly from Europe with secure digital payment systems.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  5. Track investments with updates and statements.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This solution helps Nigerians in Europe avoid these challenges while confidently growing wealth.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Future of Nigerian Diaspora Investment in Europe</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  But the future of diaspora investment is bright, and Nigerians in Europe will be crucial to shaping it. It has several factors on its side:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fintech platforms such as Diasporawise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Make it easier to invest internationally.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Renewable energy projects in green</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Are opening up sustainable options.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government incentives</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Encourage diaspora participation.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Investment sub-communities of Nigerians in Europe</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Are increasingly connected to common opportunities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Within the next decade, diaspora investors in Europe will play a much bigger role than remittances in shaping Nigeria's fundamental economic transformation.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Strengthening Nigeria</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians domiciled in Europe, investment back home is not just a financial choice – it's about contributing to national development, maintaining cultural links and wealth creation down generations.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Whether in real estate, agriculture, technology, renewable energy or SME investments trends in Nigeria, the return on investment can be impressive and diaspora investors contribute significantly to Nigeria's global market.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  With reliable services, such as those provided by Diasporawise, European Nigerians can invest without fear of losing money as they are guaranteed a safe and transparent investment process that is actually making an impact.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EuropeNigerianDiaspora;
